import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useAdminPayments } from '@dashboard/hooks/queries/useAdminQueries';

import { formatPrice } from '@dashboard/services/formatting';
import getChargeStatusLabel from '@dashboard/services/status/getChargeStatusLabel';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Checkbox, FormElement, Label } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import ViewAsMerchantButton from '@dashboard/admin/components/ViewAsMerchantButton';

import AdminZeroAmountFilter, {
  getZeroAmountFilter,
  parseZeroAmount,
} from '@dashboard/admin/components/AdminZeroAmountFilter';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'complimentary', label: 'Complimentary' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'recovered', label: 'Recovered' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'partial_refund', label: 'Partial refund' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'abandoned', label: 'Abandoned' },
];

/**
 * Charges are one model split by `pageType`: 'checkout' rows are payments,
 * 'event' rows are bookings. Complimentary only ever applies to bookings.
 */
const AdminChargesList = ({
  pageType,
  titleHeader,
  getTitle,
  testFilterLabel,
  emptyStateTitle,
  viewLabel,
  viewPath,
  supportsComplimentary = false,
}) => {
  const history = useHistory();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [livemode, setLivemode] = useState('livemode' in query ? query.livemode : true);
  const [status, setStatus] = useState(query.status || 'all');
  const [abandonmentEmailSent, setAbandonmentEmailSent] = useState(
    query.abandonmentEmailSent === true,
  );
  const [zeroAmount, setZeroAmount] = useState(parseZeroAmount(query.zeroAmount));

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        livemode,
        status,
        ...(abandonmentEmailSent ? { abandonmentEmailSent } : {}),
        ...(zeroAmount !== 'include' ? { zeroAmount } : {}),
      }),
    });
  }, [sortBy, sortDirection, livemode, status, abandonmentEmailSent, zeroAmount]);

  const filters = {
    pageType,
    sortBy,
    sortDirection,
    ...(livemode ? { livemode } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(abandonmentEmailSent ? { abandonmentEmailSent } : {}),
    ...getZeroAmountFilter(zeroAmount),
    limit: 20,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useAdminPayments(filters);

  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  const statusOptions = useMemo(
    () =>
      supportsComplimentary
        ? STATUS_OPTIONS
        : STATUS_OPTIONS.filter(option => option.value !== 'complimentary'),
    [supportsComplimentary],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'page.title',
        id: 'page.title',
        header: ({ column }) => <DataGridColumnHeader column={column} title={titleHeader} />,
        cell: ({ row }) => (
          <div className="flex items-center gap-x-2">
            <Link href={`/pages/${row.original.page._id}/customize`}>{getTitle(row.original)}</Link>
            {!row.original.livemode && (
              <Tooltip label={row.original.paymentError} side="right">
                <StatusBadge status="test" withInfoIcon={!!row.original.paymentError}>
                  Test
                </StatusBadge>
              </Tooltip>
            )}
          </div>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'amount',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Amount" />,
        cell: ({ row }) =>
          formatPrice({
            amount: row.original.amount,
            currency: row.original.currency,
          }),
        enableSorting: true,
      },
      {
        accessorKey: 'seller.displayName',
        id: 'seller.displayName',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Merchant" />,
        cell: ({ row }) => (
          <div className="relative flex items-center">
            <Link href={`/admin/merchants/${row.original.seller?._id}`}>
              {row.original.seller?.displayName || row.original.seller?.name}
            </Link>
            {row.original.seller?.disabled && (
              <Badge variant="secondary" className="ml-2">
                Disabled
              </Badge>
            )}
          </div>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
        cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM, yyyy, HH:mm'),
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-x-2">
            <Tooltip label={row.original.paymentError} side="right">
              <StatusBadge
                status={row.original.status}
                label={getChargeStatusLabel(row.original.status)}
                withInfoIcon={!!row.original.paymentError}
              />
            </Tooltip>
            {supportsComplimentary && row.original.isComplimentary && (
              <Tooltip label="Complimentary booking">
                <StatusBadge status="complimentary" label="Comp" />
              </Tooltip>
            )}
          </div>
        ),
        enableSorting: true,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <ViewAsMerchantButton
            sellerId={row.original.seller?._id}
            path={`${viewPath}/${row.original._id}`}
          >
            {viewLabel}
          </ViewAsMerchantButton>
        ),
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ],
    [titleHeader, getTitle, supportsComplimentary, viewLabel, viewPath],
  );

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <FormElement className="!mb-0">
        <div className="flex items-center gap-x-2 w-full">
          <Checkbox
            id="livemode"
            value={!livemode}
            checked={!livemode}
            onChange={() => setLivemode(livemode ? false : true)}
          />

          <Label isInline htmlFor="livemode">
            {testFilterLabel}
          </Label>
        </div>
      </FormElement>

      <FormElement className="!mb-0">
        <div className="flex items-center gap-x-2 w-full">
          <Checkbox
            id="abandonmentEmailSent"
            checked={abandonmentEmailSent}
            onChange={() => setAbandonmentEmailSent(!abandonmentEmailSent)}
          />

          <Label isInline htmlFor="abandonmentEmailSent">
            Abandonment email sent
          </Label>
        </div>
      </FormElement>

      <FormElement className="!mb-0">
        <Label htmlFor="status">Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger id="status" className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormElement>

      <AdminZeroAmountFilter value={zeroAmount} onChange={setZeroAmount} />
    </div>
  );

  return (
    <div className="pr-4">
      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        toolbar={{
          customActions: filterControls,
        }}
        emptyState={
          <EmptyState>
            <EmptyState.Title>{emptyStateTitle}</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default AdminChargesList;
